import React, { useState, useEffect } from "react";
import { TaxReturn } from "@/entities/all";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Calculator, FileText, DollarSign, CheckCircle, Edit } from "lucide-react";

export default function Review() {
  const [taxReturn, setTaxReturn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTaxReturn();
  }, []);

  const loadTaxReturn = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const returns = await TaxReturn.filter({ tax_year: currentYear }, '-created_date', 1);
      
      if (returns.length > 0) {
        setTaxReturn(returns[0]);
      }
    } catch (error) {
      console.error("Error loading tax return:", error);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your tax return...</p>
        </div>
      </div>
    );
  }

  if (!taxReturn) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Tax Return Found</h2>
            <p className="text-gray-600 mb-6">Start by creating a new tax return.</p>
            <Link to={createPageUrl("Interview")}>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Start Tax Interview
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalIncome = Object.values(taxReturn.income_info || {}).reduce((sum, val) => sum + (val || 0), 0);
  const deductionAmount = taxReturn.deductions?.standard_deduction ? 13850 : 
    Object.values(taxReturn.deductions || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
  const taxableIncome = Math.max(0, totalIncome - deductionAmount);

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Review & Calculate</h1>
          <p className="text-gray-600">
            Review your tax information and see your estimated tax liability
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <DollarSign className="w-5 h-5" />
                Total Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                ${totalIncome.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <Calculator className="w-5 h-5" />
                Deductions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                ${deductionAmount.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg bg-emerald-50 border-emerald-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                Taxable Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-700">
                ${taxableIncome.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Tax Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {taxReturn.calculated_tax ? (
                <>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Federal Tax:</span>
                    <span className="font-semibold text-lg">
                      ${taxReturn.calculated_tax.federal_tax?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">California State Tax:</span>
                    <span className="font-semibold text-lg">
                      ${taxReturn.calculated_tax.state_tax?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-4 bg-emerald-50 px-4 rounded-lg">
                    <span className="font-bold text-lg">Total Tax Liability:</span>
                    <span className="font-bold text-2xl text-emerald-700">
                      ${taxReturn.calculated_tax.total_tax?.toLocaleString()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Complete the tax interview to calculate your taxes
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Next Steps</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to={createPageUrl("Interview")}>
                <Button variant="outline" className="w-full justify-start">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Tax Return
                </Button>
              </Link>
              <Link to={createPageUrl("Forms")}>
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700">
                  View Tax Forms
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}